import type { RouteObject } from "react-router";
import IssueReportProvider from "../features/issueReports/IssueReportProvider";
import ItemNameProvider from "../features/items/ItemNameProvider";
import SourceDocumentProvider from "../features/sources/document/SourceDocumentProvider";
import CorpusSelectionProvider from "../features/sources/corpus/CorpusSelectionProvider";
import ThreadLiveProvider from "../features/threads/live/ThreadLiveProvider";
import ProjectProvider from "../projects/ProjectProvider";
import NewerBuildNotice from "../serverStatus/NewerBuildNotice";
import TopBar from "./TopBar";
import ChromeRouter from "./workspace/ChromeRouter";
import TabLayoutView from "./workspace/tabs/TabLayoutView";
import { useWorkspaceOwner } from "./workspace/useWorkspaceOwner";
import type { WorkspaceWindow } from "./workspace/workspaceController";
import { WorkspaceContext } from "./workspace/workspaceContext";

interface AppShellProps {
  readonly browser: WorkspaceWindow;
  /** The pages a work tab routes. */
  readonly tabRoutes: readonly RouteObject[];
  readonly accountId: string;
}

/**
 * The working app (web-app.md WA-10, F90): one top bar over the whole width and the work tabs
 * in groups under it; each tab draws its own section's sidebar in its pane. The workspace
 * owner places every page; the window has no router of its own here, each tab has one. The
 * providers sit above every tab, so the tabs share one live socket for Threads and one set of
 * caches.
 *
 * Scrolling (F45): each tab's page and sidebar scroll on their own; the window never does.
 * Everything around them clips (`overflow-clip`) instead of hiding overflow: a hidden overflow
 * can still be scrolled by code, and a page's `scrollIntoView` scrolls every container around
 * it. `app-workspace` clips the document too (theme/base/layout.css).
 */
export default function AppShell(props: AppShellProps) {
  return (
    <ProjectProvider>
      <ItemNameProvider>
        <SourceDocumentProvider>
          <CorpusSelectionProvider>
            <ThreadLiveProvider>
              <Workspace {...props} />
            </ThreadLiveProvider>
          </CorpusSelectionProvider>
        </SourceDocumentProvider>
      </ItemNameProvider>
    </ProjectProvider>
  );
}

function Workspace({ browser, tabRoutes, accountId }: AppShellProps) {
  const owner = useWorkspaceOwner(browser, accountId);

  return (
    <WorkspaceContext.Provider value={owner.chrome}>
      <IssueReportProvider>
        <div className="app-workspace app-shell flex h-dvh flex-col overflow-clip">
          <ChromeRouter owner={owner}>
            <TopBar owner={owner} />
          </ChromeRouter>
          <NewerBuildNotice />
          <div className="app-tabs relative min-h-0 min-w-0 flex-1">
            <TabLayoutView controller={owner.controller} tabRoutes={tabRoutes} />
          </div>
        </div>
      </IssueReportProvider>
    </WorkspaceContext.Provider>
  );
}
